import React from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import Footer from '../Footer';
import Header from '../Header';
import MetaTags from '../MetaTags';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import { responsive } from '../../lib/responsive';
import type {
    LegalBlock,
    LegalDocument,
    LegalInlineSegment,
    LegalListBlock,
} from '../../content/legal/types';

function isListBlock(block: LegalBlock): block is LegalListBlock {
    return block.type === 'list'
        || block.type === 'ordered-list'
        || block.type === 'plain-list';
}

function InlineText({ segments }: { segments: readonly LegalInlineSegment[] }) {
    return <>{segments.map((segment, index) => (
        <Text
            key={`${index}-${segment.text}`}
            style={[
                segment.bold && styles.bold,
                segment.italic && styles.italic,
            ]}
        >
            {segment.text}
        </Text>
    ))}</>;
}

function headingProps(level: 1 | 2 | 3) {
    return {
        accessibilityRole: 'header' as const,
        ...(Platform.OS === 'web' && { role: 'heading' as const, 'aria-level': level }),
    };
}

function LegalBody({ document }: { document: LegalDocument }) {
    const { fontsLoaded } = useFontLoading();
    const h2 = [styles.sectionTitle, fontsLoaded && interSemiBold];
    const h3 = [styles.subsectionTitle, fontsLoaded && interSemiBold];
    const paragraph = [styles.sectionText, fontsLoaded && interRegular];
    const listItem = [styles.bulletPoint, fontsLoaded && interRegular];

    return <>
        {document.blocks.map((block, blockIndex) => {
            if (block.type === 'heading') {
                const level = block.level === 3 ? 3 : 2;
                return (
                    <Text
                        key={blockIndex}
                        style={level === 3 ? h3 : h2}
                        {...headingProps(level)}
                    >
                        <InlineText segments={block.segments} />
                    </Text>
                );
            }
            if (isListBlock(block)) {
                return (
                    <View key={blockIndex} style={styles.bulletList}>
                        {block.items.map((segments, itemIndex) => (
                            <Text key={itemIndex} style={listItem}>
                                {block.type === 'ordered-list'
                                    ? `${itemIndex + 1}. `
                                    : block.type === 'plain-list' ? '' : '\u2022 '}
                                <InlineText segments={segments} />
                            </Text>
                        ))}
                    </View>
                );
            }
            if (block.type === 'tagline') {
                return (
                    <Text key={blockIndex} style={[styles.tagline, fontsLoaded && interSemiBold]}>
                        <InlineText segments={block.segments} />
                    </Text>
                );
            }
            return (
                <Text key={blockIndex} style={paragraph}>
                    <InlineText segments={block.segments} />
                </Text>
            );
        })}
        {document.signature && (
            <Text style={[styles.signature, fontsLoaded && interRegular]}>{document.signature}</Text>
        )}
    </>;
}

function IncidentBody({ document }: { document: LegalDocument }) {
    const { fontsLoaded } = useFontLoading();
    const sections: LegalDocument['blocks'][] = [];
    for (const block of document.blocks) {
        if (block.type === 'heading') sections.push([block]);
        else sections.at(-1)?.push(block);
    }

    return <>
        {sections.map((section, sectionIndex) => (
            <View key={sectionIndex} style={styles.incidentSection}>
                {section.map((block, blockIndex) => {
                    if (block.type === 'heading') {
                        return (
                            <Text
                                key={blockIndex}
                                style={[styles.incidentSectionTitle, fontsLoaded && interSemiBold]}
                                {...headingProps(2)}
                            >
                                <InlineText segments={block.segments} />
                            </Text>
                        );
                    }
                    if (isListBlock(block)) {
                        return (
                            <View key={blockIndex} style={styles.incidentBulletList}>
                                {block.items.map((segments, itemIndex) => (
                                    <Text
                                        key={itemIndex}
                                        style={[styles.incidentBulletPoint, fontsLoaded && interRegular]}
                                    >
                                        {'\u2022'} <InlineText segments={segments} />
                                    </Text>
                                ))}
                            </View>
                        );
                    }
                    if (block.type === 'tagline') {
                        return (
                            <Text key={blockIndex} style={[styles.tagline, fontsLoaded && interSemiBold]}>
                                <InlineText segments={block.segments} />
                            </Text>
                        );
                    }
                    return (
                        <Text key={blockIndex} style={[styles.incidentSectionText, fontsLoaded && interRegular]}>
                            <InlineText segments={block.segments} />
                        </Text>
                    );
                })}
                {sectionIndex === sections.length - 1 && document.signature && (
                    <Text style={[styles.signature, fontsLoaded && interRegular]}>{document.signature}</Text>
                )}
            </View>
        ))}
    </>;
}

function LegalPage({ document }: { document: LegalDocument }) {
    const { fontsLoaded } = useFontLoading();
    return (
        <View style={styles.page}>
            <Header />
            <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
                <View style={styles.legalContainer} {...(Platform.OS === 'web' && { lang: document.contentLocale })}>
                    <Text
                        style={[styles.pageTitle, fontsLoaded && interSemiBold]}
                        {...headingProps(1)}
                    >
                        {document.pageTitle}
                    </Text>
                    {document.effectiveDate && (
                        <Text style={[styles.effectiveDate, fontsLoaded && interMedium]}>
                            {document.effectiveDate}
                        </Text>
                    )}
                    <LegalBody document={document} />
                    <View style={styles.bottomSpacer} />
                </View>
                <Footer />
            </ScrollView>
        </View>
    );
}

function IncidentPage({ document }: { document: LegalDocument }) {
    const { fontsLoaded } = useFontLoading();
    return (
        <View style={styles.page}>
            <Header />
            <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
                <LinearGradient
                    colors={['#ffffff', '#fbfcff', '#f6f7fb']}
                    start={{ x: 0.5, y: 0 }}
                    end={{ x: 0.5, y: 1 }}
                    style={styles.heroGradient}
                >
                    <View style={styles.incidentContainer} {...(Platform.OS === 'web' && { lang: document.contentLocale })}>
                        <Text
                            style={[styles.incidentPageTitle, fontsLoaded && interSemiBold]}
                            {...headingProps(1)}
                            {...responsive('section-title')}
                        >
                            {document.pageTitle}
                        </Text>
                        {document.subtitle && (
                            <Text style={[styles.subtitle, fontsLoaded && interRegular]}>
                                {document.subtitle}
                            </Text>
                        )}
                    </View>
                </LinearGradient>
                <View style={styles.incidentContainer} {...(Platform.OS === 'web' && { lang: document.contentLocale })}>
                    <View style={styles.metaSection}>
                        {document.incidentMeta?.map((segments, index) => (
                            <Text key={index} style={[styles.metaItem, fontsLoaded && interRegular]}>
                                <InlineText segments={segments} />
                            </Text>
                        ))}
                    </View>
                    <IncidentBody document={document} />
                </View>
                <Footer />
            </ScrollView>
        </View>
    );
}

export default function LongFormDocument({ document }: { document: LegalDocument }) {
    return <>
        <MetaTags contentLocale={document.contentLocale}
            surfaceId={document.id}
            title={document.metaTitle}
            description={document.metaDescription}
        />
        {document.variant === 'incident'
            ? <IncidentPage document={document} />
            : <LegalPage document={document} />}
    </>;
}

const styles = StyleSheet.create({
    page: { flex: 1, backgroundColor: colors.bgWeak },
    scrollView: { flex: 1 },
    legalContainer: {
        width: '100%', maxWidth: 800, alignSelf: 'center', paddingHorizontal: 24,
    },
    incidentContainer: {
        width: '100%', maxWidth: 1280, alignSelf: 'center', paddingHorizontal: 24,
    },
    pageTitle: {
        fontSize: 40, fontWeight: '600', color: colors.primaryGreenDark,
        marginBottom: 8, marginTop: 32,
    },
    incidentPageTitle: {
        fontSize: 48, fontWeight: '600', color: colors.primaryGreenDark,
        marginBottom: 8, marginTop: 16,
    },
    effectiveDate: { fontSize: 14, color: colors.text500, marginBottom: 32 },
    subtitle: { fontSize: 20, color: colors.text700, marginBottom: 32 },
    sectionTitle: {
        fontSize: 22, fontWeight: '600', color: colors.text900,
        marginTop: 40, marginBottom: 12,
    },
    subsectionTitle: {
        fontSize: 17, fontWeight: '600', color: colors.text900,
        marginTop: 24, marginBottom: 8,
    },
    sectionText: {
        fontSize: 15, color: colors.text700, lineHeight: 24, marginBottom: 12,
    },
    bold: { fontWeight: '600', color: colors.text900 },
    italic: { fontStyle: 'italic' },
    bulletList: { marginTop: 4, marginBottom: 12, gap: 6 },
    bulletPoint: {
        fontSize: 15, color: colors.text700, lineHeight: 24, paddingLeft: 8,
    },
    bottomSpacer: { height: 48 },
    heroGradient: { width: '100%' },
    metaSection: {
        backgroundColor: colors.bgWeak, padding: 20, borderRadius: 12,
        marginBottom: 32, borderWidth: 1, borderColor: colors.border,
    },
    metaItem: { fontSize: 16, color: colors.text700, lineHeight: 28 },
    incidentSection: { marginBottom: 48 },
    incidentSectionTitle: {
        fontSize: 24, fontWeight: '600', color: colors.primaryGreenDark, marginBottom: 16,
    },
    incidentSectionText: {
        fontSize: 16, color: colors.text700, lineHeight: 26, marginBottom: 12,
    },
    incidentBulletList: { marginTop: 8, gap: 12 },
    incidentBulletPoint: { fontSize: 16, color: colors.text700, lineHeight: 26 },
    tagline: {
        fontSize: 20, fontWeight: '600', color: colors.primaryGreen,
        marginVertical: 16, textAlign: 'center',
    },
    signature: {
        fontSize: 16, color: colors.text700,
        marginTop: 24, fontStyle: 'italic',
    },
});
